import { Divider, Grid } from '@mui/material';
import { EnrichmentRecordType, getDataAnomalyFieldRecommendation } from 'http/ai';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { SmartRecommendationLoading } from '../shared/SmartRecommendationLoading';
import { SmartRecommendationField } from '../DataAnomalyCardFieldDetail/SmartRecommendationField';
import { DataRaptorRule } from 'store/dataRaptorRule/types';
import { AiRecommendation, FieldRecommendation, OnDemandFieldEnrichmentStatus } from 'store/dataAnomaly/types';
import { Show } from '../../../../../../../../../../../../../../components/show';
import { RecommendationFeedBack } from '../DataAnomalyCardFieldDetail/RecommendationFeedBack';
import { ActionRecommendationsList } from './ActionRecommendationList';
import { FieldRecommendationList } from './FieldRecommendationList/FieldRecommendationList';
import { useLazyGetMigrationTableDataByIdQuery } from 'store/migration/api';

export interface RuleSmartRecommendationProps {
  summary: string[];
  recommendationId: string;
  migrationId: string;
  fieldRecommendations?: FieldRecommendation[];
  actionRecommendations?: string[];
  rule: DataRaptorRule;
  node: any;
  aiRecommendation?: AiRecommendation;
}

export const RuleSmartRecommendation: FC<RuleSmartRecommendationProps> = ({
  summary,
  migrationId,
  rule,
  node,
  fieldRecommendations,
  actionRecommendations,
  recommendationId,
  aiRecommendation,
}) => {
  const [loadingRecommendation, setLoadingRecommendation] = useState<boolean>(false);
  const [onDemandDataStatus, setOnDemandDataStatus] = useState<OnDemandFieldEnrichmentStatus | undefined>(undefined);
  const [timeStampPendingStatus, setTimeStampPendingStatus] = useState<string | undefined>(new Date().toISOString());
  const [loadedRecommendations, setLoadedRecommendations] = useState<FieldRecommendation[] | undefined>(undefined);
  const [loadedActionRecommendations, setLoadedActionRecommendations] = useState<string[] | undefined>(undefined);
  const { enqueueSnackbar } = useSnackbar();

  const isCompanyNode = useMemo(() => node.label === 'Account', [node.label]);

  const [getMigrationTableDataByIdQuery, accountRecord] = useLazyGetMigrationTableDataByIdQuery();

  useEffect(() => {
    if (isCompanyNode === false && node.AccountId) {
      getMigrationTableDataByIdQuery({
        migrationId,
        tableId: 'Account',
        conditions: [{ field: '"Id"', operator: '=', value: `'${node.AccountId}'` }],
        skip: 0,
        take: 1,
      });
    }
  }, [getMigrationTableDataByIdQuery, isCompanyNode, migrationId, node.AccountId]);

  const calculatedFieldRecommendations = useMemo(() => {
    return loadedRecommendations || fieldRecommendations;
  }, [fieldRecommendations, loadedRecommendations]);

  const calculatedActionRecommendations = useMemo(() => {
    return loadedActionRecommendations || actionRecommendations;
  }, [actionRecommendations, loadedActionRecommendations]);

  const haveRecommendation = useMemo(() => {
    return (
      (Array.isArray(calculatedFieldRecommendations) && calculatedFieldRecommendations.length > 0) ||
      (Array.isArray(calculatedActionRecommendations) && calculatedActionRecommendations.length > 0)
    );
  }, [calculatedActionRecommendations, calculatedFieldRecommendations]);

  const handleGetAnomalyFieldRecommendation = useCallback(() => {
    setLoadingRecommendation(true);
    let timeOutRef: NodeJS.Timeout;
    getDataAnomalyFieldRecommendation(migrationId, {
      ruleId: rule.ruleId,
      record: node,
      details: summary,
      recordId: node.id,
      tableName: node.label,
      enrichmentContext: {
        ...(isCompanyNode && { name: node.Name }),
        ...(isCompanyNode && { industry: node.Industry }),
        ...(!isCompanyNode && { fullName: node.Name || `${node.FirstName} ${node.LastName}` }),
        ...(!isCompanyNode && { email: node.Email }),
        ...(!isCompanyNode &&
          accountRecord &&
          accountRecord.isSuccess &&
          accountRecord.data &&
          accountRecord.data[0].Name && { companyName: accountRecord.data[0].Name }),
      },
      enrichmentRecordType: isCompanyNode ? EnrichmentRecordType.COMPANY : EnrichmentRecordType.PROFILE,
    })
      .then((res) => {
        console.log('AI Field Recommendation res', res);
        setLoadedRecommendations(res.recommendation.fieldRecommendations);
        setLoadedActionRecommendations(res.recommendation.actionRecommendations);
        setOnDemandDataStatus(res.recommendation?.onDemandFieldEnrichmentStatus);
        if (res.recommendation?.onDemandFieldEnrichmentStatus === OnDemandFieldEnrichmentStatus.PENDING) {
          setTimeStampPendingStatus(res.recommendation?.onDemandFieldEnrichmentStatusTimeStamp);
          timeOutRef = setTimeout(() => {
            handleGetAnomalyFieldRecommendation();
          }, 5000);
        } else {
          setTimeStampPendingStatus(undefined);
        }
      })
      .catch((err) => {
        enqueueSnackbar('Failed to get recommendation', { variant: 'error' });
        console.log('AI Field Recommendation err', err);
      })
      .finally(() => setLoadingRecommendation(false));
    return () => clearTimeout(timeOutRef);
  }, [accountRecord, enqueueSnackbar, isCompanyNode, migrationId, node, rule.ruleId, summary]);

  useEffect(() => {
    if (aiRecommendation?.payload.onDemandFieldEnrichmentStatus === OnDemandFieldEnrichmentStatus.PENDING) {
      handleGetAnomalyFieldRecommendation();
      setOnDemandDataStatus(aiRecommendation?.payload.onDemandFieldEnrichmentStatus);
      setTimeStampPendingStatus(aiRecommendation?.payload.onDemandFieldEnrichmentStatusTimeStamp);
    }
  }, []); // Call only on first render

  const getRecommendationComponent = useCallback(() => {
    if (!haveRecommendation && onDemandDataStatus === OnDemandFieldEnrichmentStatus.PENDING) {
      return <SmartRecommendationLoading intervalInSeconds={900} startTime={timeStampPendingStatus} />;
    }
    if (
      loadingRecommendation === false &&
      !haveRecommendation &&
      onDemandDataStatus !== OnDemandFieldEnrichmentStatus.PENDING
    ) {
      return (
        <SmartRecommendationField
          handleGetAnomalyFieldRecommendation={handleGetAnomalyFieldRecommendation}
          recommendationCalculated={
            (calculatedFieldRecommendations && calculatedFieldRecommendations?.[0]?.value) || ''
          }
        />
      );
    }
    if (loadingRecommendation === true) {
      return <SmartRecommendationLoading />;
    }
  }, [
    calculatedFieldRecommendations,
    handleGetAnomalyFieldRecommendation,
    haveRecommendation,
    loadingRecommendation,
    onDemandDataStatus,
    timeStampPendingStatus,
  ]);

  const SmartFieldComponent = useMemo(() => getRecommendationComponent(), [getRecommendationComponent]);

  return (
    <>
      {/* Smart Recommendation */}
      <Grid container sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Grid item>{SmartFieldComponent}</Grid>
        {/* Field Recommendation List */}
        <Grid>
          <Show when={Array.isArray(calculatedFieldRecommendations) && calculatedFieldRecommendations.length > 0}>
            <FieldRecommendationList
              fieldRecommendations={calculatedFieldRecommendations || []}
              node={node}
              migrationId={migrationId}
              recommendationId={recommendationId}
              summary={summary[0]}
            />
          </Show>
        </Grid>
        {/* Actions Recommendation List */}
        <Grid>
          <Show when={Array.isArray(calculatedActionRecommendations) && calculatedActionRecommendations.length > 0}>
            <ActionRecommendationsList
              actionRecommendations={calculatedActionRecommendations || []}
              migrationId={migrationId}
              recommendationId={recommendationId}
              summary={summary[0]}
              node={node}
            />
          </Show>
        </Grid>
        {/* Divider */}
        <Divider sx={{ width: '100%' }} />
        {/* AI Recommendation Feedback */}
        <Show when={haveRecommendation}>
          <RecommendationFeedBack
            recommendationId={recommendationId}
            migrationId={migrationId}
          ></RecommendationFeedBack>
        </Show>
      </Grid>
    </>
  );
};
